'use client';

import React, { useCallback, useMemo } from 'react';
import { cn } from '@/lib/cn';
import { SectionHeader, SelectionCell, Spinner } from '@/components/ui';
import { IconBuilding, IconLocation, IconLocationPin, IconMapLocation } from '@repo/svgs';
import { useStore } from '@/providers/StoreProvider';
import { useAnalytics } from '@/hooks/useAnalytics';
import {
  SuggestedLocations as SuggestedLocationsData,
  type SuggestedLocation,
} from '../../shared/constants';
import { deriveBoroughFromAddress } from '@repo/utils';

export interface SuggestedLocationsProps {
  locations?: SuggestedLocation[];
  className?: string;
}

/**
 * SuggestedLocations — matches Figma node `752:14048`.
 */
export function SuggestedLocations({
  locations = SuggestedLocationsData,
  className,
}: SuggestedLocationsProps) {
  const analytics = useAnalytics();
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const building = useStore(s => s.listingsFilters.building);
  const setBuilding = useStore(s => s.listingsFilters.setBuilding);
  const resetBuilding = useStore(s => s.listingsFilters.resetBuilding);
  const toggleNeighborhood = useStore(s => s.listingsFilters.toggleNeighborhood);
  const toggleNeighborhoodWithChildren = useStore(
    s => s.listingsFilters.toggleNeighborhoodWithChildren,
  );
  const toggleBorough = useStore(s => s.listingsFilters.toggleBorough);
  const accordion = useStore(s => s.neighborhoods.neighborhoodAccordionData);
  const isNeighborhoodsLoading = useStore(s =>
    s.loading.isOperationLoading('neighborhoods.fetchNeighborhoods'),
  );

  const isDisabled = isNeighborhoodsLoading;

  const selectedSet = useMemo(() => new Set(neighborhoodIds), [neighborhoodIds]);
  const isBoroughFullySelected = useCallback(
    (borough: string) => {
      const entry = (accordion as any)?.[borough];
      if (!entry) return false;
      const allIds: string[] = [];
      for (const p of entry.parents || []) allIds.push(p.id);
      for (const group of Object.values(entry.childrenByParent || {})) {
        for (const c of group as any[]) allIds.push(c.id);
      }
      return allIds.length > 0 && allIds.every(id => selectedSet.has(id));
    },
    [accordion, selectedSet],
  );

  const hasSuggestedLocations = Array.isArray(locations) && locations.length > 0;
  if (!hasSuggestedLocations) return null;

  const handleToggleBorough = (borough: string, isSelected: boolean) => {
    analytics.track(
      isSelected ? 'listings_filter_borough_unselect' : 'listings_filter_borough_select',
      { boroughName: borough },
    );
    toggleBorough(borough);
  };

  const handleToggleNeighborhood = (args: {
    id: string;
    name: string;
    borough: string;
    isSelected: boolean;
    hasChildren: boolean;
  }) => {
    const { id, name, isSelected, hasChildren } = args;
    analytics.track(
      isSelected ? 'listings_filter_neighborhood_unselect' : 'listings_filter_neighborhood_select',
      { neighborhoodId: id, neighborhoodName: name },
    );
    if (hasChildren) toggleNeighborhoodWithChildren(id);
    else toggleNeighborhood(id);
  };

  const handleToggleSuggestedBuilding = (args: {
    addressSlug: string;
    buildingSlug: string;
    label: string;
    isSelected: boolean;
  }) => {
    const { addressSlug, buildingSlug, label, isSelected } = args;
    analytics.track('listings_filter_building');
    if (isSelected) {
      resetBuilding();
      return;
    }
    setBuilding({
      addressSlug,
      slug: buildingSlug,
      address: label,
    });
  };

  return (
    <div className={cn(className)}>
      {/* Matches Figma "Suggested" header (no pill) */}
      <SectionHeader
        titleClassName="text-heading-6 text-primary -mx-2"
        title="Suggested"
        trailingIcon={
          isDisabled ? (
            <div className="w-4 h-4 ml-2 flex items-center justify-center">
              <Spinner size="md" />
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-1">
        {locations.map(location => {
          if (location.type === 'borough') {
            const allSelected = isBoroughFullySelected(location.borough);
            return (
              <SelectionCell
                key={`borough:${location.borough}`}
                title={location.borough}
                subtitle="Borough"
                selected={allSelected}
                disabled={isDisabled}
                leadingIcon={<IconMapLocation className="w-6 h-6 icon-secondary" />}
                onToggle={() => handleToggleBorough(location.borough, allSelected)}
              />
            );
          }

          if (location.type === 'address') {
            const borough = deriveBoroughFromAddress(location.fullAddress);
            const isSelected =
              building?.slug === location.buildingSlug || building?.addressSlug === location.slug;
            return (
              <SelectionCell
                key={`suggested-address:${location.buildingId}:${location.slug}`}
                title={location.streetAddress}
                subtitle={`Address${borough ? ` in ${borough}` : ''}`}
                selected={isSelected}
                disabled={isDisabled}
                leadingIcon={<IconLocationPin className="w-6 h-6 icon-secondary" />}
                onToggle={() =>
                  handleToggleSuggestedBuilding({
                    addressSlug: location.slug,
                    buildingSlug: location.buildingSlug,
                    label: location.streetAddress,
                    isSelected,
                  })
                }
              />
            );
          }

          if (location.type === 'building') {
            const borough = deriveBoroughFromAddress(location.fullAddress);
            const isSelected =
              building?.slug === location.buildingSlug || building?.addressSlug === location.slug;
            return (
              <SelectionCell
                key={`suggested-building:${location.buildingId}:${location.slug}`}
                title={location.name}
                subtitle={`Building${borough ? ` in ${borough}` : ''}`}
                selected={isSelected}
                disabled={isDisabled}
                leadingIcon={<IconBuilding className="w-6 h-6 icon-secondary" />}
                onToggle={() =>
                  handleToggleSuggestedBuilding({
                    addressSlug: location.slug,
                    buildingSlug: location.buildingSlug,
                    label: location.name,
                    isSelected,
                  })
                }
              />
            );
          }

          const isSelected =
            Array.isArray(neighborhoodIds) && neighborhoodIds.includes(location.id);
          const hasChildren =
            Array.isArray(location.childrenIds) && location.childrenIds.length > 0;
          return (
            <SelectionCell
              key={location.id}
              title={location.name}
              subtitle={`Neighborhood in ${location.borough}`}
              selected={isSelected}
              disabled={isDisabled}
              leadingIcon={<IconLocation className="w-6 h-6 icon-secondary" />}
              onToggle={() =>
                handleToggleNeighborhood({
                  id: location.id,
                  name: location.name,
                  borough: location.borough,
                  isSelected,
                  hasChildren,
                })
              }
            />
          );
        })}
      </div>
    </div>
  );
}

export default SuggestedLocations;
